"use client";

import { Codex, Group, Media } from "@/src/payload-types";
import React, { useMemo } from "react";
import { RichText } from "@payloadcms/richtext-lexical/react";
import jsxConverters, { collectSources, SourceIndexContext, useIsMobile } from "@/src/blocks/converters";
import SourceList from "@/src/components/SourceList";
import { InfoPoint } from "@/src/components/ConsumerMediaDetail";
import { Landmark } from "lucide-react";
import { getBlobUrl, getGroupLink } from "@/src/components/utils/utils";
import { SheetContainer } from "@/src/components/Containers";
import Link from "next/link";

const CodexDetail = ({ codex, inSheet = true }: { codex: Codex; inSheet: boolean }) => {
    // const [previewContent, setPreviewContent] = useState<CharacterContent[]>();
    //
    // useEffect(() => {
    //     const handleMessage = async (event: MessageEvent) => {
    //         // Only accept messages from Payload admin
    //         if (event.data?.type === "payload-live-preview" && event.data.data) {
    //             const tempContent = content.map((c) => (c.id === event.data.data.id ? event.data.data : c));
    //             setPreviewContent(sortContent(tempContent));
    //         }
    //     };
    //
    //     window.addEventListener("message", handleMessage);
    //     if (content.length) {
    //         setTab(content[0].id.toString());
    //     }
    //     return () => window.removeEventListener("message", handleMessage);
    // }, [content]);

    // function getContentComponent(c: CharacterContent) {
    //     switch (c.type) {
    //         case "APPEARANCES":
    //             return <Appearances consumerMediaEntries={sortedEntries} />;
    //         case "FREEFORM":
    //             return <div />;
    //         case "SUMMARY":
    //             return <ContentSummary character={character} content={c} />;
    //         case "RELATIONSHIPS":
    //             return <ContentRelationships character={character} content={c} connections={connections} />;
    //     }
    // }

    const splash = (codex.splash as Media)?.sizes?.splash?.url
        ? (codex.splash as Media)?.sizes?.splash
        : (codex.splash as Media)?.sizes?.avif;
    const isMobile = useIsMobile();

    const image = splash && getBlobUrl(splash.url!);
    return (
        <SheetContainer inSheet={inSheet} img={image}>
            <Link className="z-20 mt-6 pl-8 mb-[-22px]" href={`/codex?selection=${(codex.group as Group)?.slug}`}>
                Back to codex
            </Link>
            <div className="pr-8 pl-8 pt-0 pb-0 shrink-0">
                <CodexHeader codex={codex} splash={splash} image={image} />
            </div>
            <div className="relative pl-8 pr-8">
                {/*Name*/}
                <CodexContent codex={codex} />
                {/*{contentToUse?.length > 0 && (*/}
                {/*    <div>*/}
                {/*        <Tabs key={tab} value={tab} onValueChange={setTab}>*/}
                {/*            <div className="flex pb-4 pt-4">*/}
                {/*                <TabsList>*/}
                {/*                    {contentToUse?.map?.((c) => (*/}
                {/*                        <TabsTrigger*/}
                {/*                            className={`hover:cursor-pointer ${isMobile ? "text-sm p-1" : "text-xl p-5"}`}*/}
                {/*                            key={c.id}*/}
                {/*                            value={c.id.toString()}*/}
                {/*                        >*/}
                {/*                            {c.title || c.type}*/}
                {/*                        </TabsTrigger>*/}
                {/*                    ))}*/}
                {/*                </TabsList>*/}
                {/*            </div>*/}
                {/*            {contentToUse?.map?.((c) => (*/}
                {/*                <TabsContent*/}
                {/*                    forceMount*/}
                {/*                    style={*/}
                {/*                        tab !== c.id.toString()*/}
                {/*                            ? { visibility: "hidden", height: 0, position: "absolute" }*/}
                {/*                            : {}*/}
                {/*                    }*/}
                {/*                    key={c.id}*/}
                {/*                    value={c.id.toString()}*/}
                {/*                >*/}
                {/*                    {getContentComponent(c)}*/}
                {/*                </TabsContent>*/}
                {/*            ))}*/}
                {/*        </Tabs>*/}
                {/*    </div>*/}
                {/*)}*/}
            </div>
        </SheetContainer>
    );
};

const CodexContent = ({ codex }: { codex: Codex }) => {
    const sourceList = useMemo(() => collectSources(codex.content), [codex.content]);
    const sourceIndex = useMemo(() => new Map(sourceList.map((s) => [s.id, s.index])), [sourceList]);

    return (
        <div>
            {/*<TableOfContents content={codex.content} />*/}
            <div className="pt-8">
                <SourceIndexContext.Provider value={sourceIndex}>
                    {codex.content && <RichText converters={jsxConverters} data={codex.content} />}
                </SourceIndexContext.Provider>
            </div>
            <SourceList sources={sourceList} />
        </div>
    );
};

interface CodexHeaderProps {
    codex: Codex;
    image: undefined | string;
    splash?:
        | {
              url?: string | null;
              width?: number | null;
              height?: number | null;
              mimeType?: string | null;
              filesize?: number | null;
              filename?: string | null;
          }
        | undefined;
}

const CodexHeader = ({ codex, splash, image }: CodexHeaderProps) => {
    const isSmallScreen = useIsMobile(1050);

    return isSmallScreen ? (
        <MobileHeader codex={codex} splash={splash} image={image} />
    ) : (
        <DesktopHeader codex={codex} splash={splash} image={image} />
    );
};

const DesktopHeader = ({ splash, image, codex }: CodexHeaderProps) => {
    return (
        <>
            <div className="relative flex items-top">
                {/*cover*/}
                <div className="basis-1/2">
                    {splash && <img src={image} alt={(codex.splash as Media).alt} className="rounded-xl" />}
                </div>
                {/*Info*/}
                <div className="basis-1/3 pt-8 pl-8 flex flex-col gap-4">
                    <InfoPoint title="FACTION" value={getGroupLink(codex.group as Group)} icon={<Landmark />} />
                </div>
            </div>
            <div className="relative flex flex-col basis-2/3">
                {/*Title*/}
                <div className="pb-4">
                    <div className="text-4xl font-bold">{codex.name}</div>
                    <div className="text-xl">{codex.category}</div>
                </div>
            </div>
        </>
    );
};

const MobileHeader = ({ splash, image, codex }: CodexHeaderProps) => {
    return (
        <>
            <div className="relative flex flex-col items-top">
                {/*cover*/}
                <div className="basis-1/2">
                    {splash && <img src={image} alt={(codex.splash as Media).alt} className="rounded-xl" />}
                </div>
                <div className="relative flex flex-col basis-2/3">
                    {/*Title*/}
                    <div className="pb-4">
                        <div className="text-4xl font-bold">{codex.name}</div>
                        <div className="text-xl">{codex.category}</div>
                    </div>
                </div>
                {/*Info*/}
                <div className="basis-1/3 flex flex-col gap-4">
                    <InfoPoint title="FACTION" value={getGroupLink(codex.group as Group)} icon={<Landmark />} />
                </div>
            </div>
        </>
    );
};

export default CodexDetail;
